/* eslint-disable camelcase */
import { Accordion, AccordionDetails, AccordionSummary, Grid, Stack, Tooltip, Typography } from '@mui/material'
import AgriAction from 'agriAdministration/actions/AgriAction'
import Input from 'components/forms/Input'
import ProgressCard from 'components/card/ProgressCard'
import { push } from '@lagunovsky/redux-react-router'
import InstallationAction from 'installation/actions/InstallationAction'
import { compact, orderBy } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import { formatMilliers, getI18nTitleData, normalizeString } from 'utils/StringUtil'
import { POINT_STATUS_DECLARATION } from 'agriAdministration/constants/AgriConstants'
import { hasValue } from 'utils/NumberUtil'
import { Delete, People } from '@mui/icons-material'
import { useParams } from 'react-router'
import { NoDataPanel } from 'components/card/NoDataCard'


const DeclarationPointsStep = () => {
    const [pointsLoaded, setPointsLoaded] = useState(false)
    const [searchValue, setSearchValue] = useState()

    const {
        exploitation,
        installationsWithGeo,
        citiesIndex,
        variousMateriels,
        exploitationsExportFullData,
        declaration,
    } = useSelector(store => ({
        exploitation: store.AgriReducer.exploitation,
        installationsWithGeo: store.InstallationReducer.installationsWithGeo,
        citiesIndex: store.CityReducer.citiesIndex,
        variousMateriels: store.VariousMaterielReducer.variousMateriels,
        exploitationsExportFullData: store.AgriReducer.exploitationsExportFullData,
        declaration: store.AgriReducer.declaration,
    }), shallowEqual)

    const dispatch = useDispatch()
    const params = useParams()

    const selectedPoints = useMemo(() => declaration.link_declarationInstallation || [], [declaration])

    useEffect(() => {
        dispatch(AgriAction.fetchExploitationUser()).then(() => {
            if (!installationsWithGeo.length) {
                dispatch(InstallationAction.fetchInstallationsWithGeo()).then(() => setPointsLoaded(true))
            } else {
                setPointsLoaded(true)
            }
        })
    }, [])

    const getLineRepartition = (link) => {
        const exploitationFound = exploitationsExportFullData.find((e) => e.idExploitation === link.idExploitation) || {}
        return (
            <Typography>{exploitationFound.codification} : {hasValue(link.distribution) ? `${link.distribution} %` : i18n.unknown}</Typography>
        )
    }

    const getPointCard = (installation) => {
        if (installation) {
            const linkPoint = exploitation.link_samplings.find((l) => l.idInstallation === installation.id) || {}
            const link_shared = linkPoint.link_shared || []
            const pointIsShared = link_shared.filter((p) => p.idExploitation !== exploitation.idExploitation).length > 0
            const total = link_shared.reduce((acc, l) => acc + (l.distribution || 0), 0)
            const tooltip = (
                <Stack spacing={0.5}>
                    {link_shared.map((p) => getLineRepartition(p))}
                    <Typography sx={{ fontWeight: 'bold' }}>{i18n.total} : {`${total} %`}</Typography>
                </Stack>
            )
            const totalUsage = installation.link_usages.reduce((acc, u) => acc + (isNaN(parseInt(u.requestedYearVolume)) ? 0 : u.requestedYearVolume), 0)
            return (
                <Grid
                    container
                    size={12}
                    sx={{
                        borderBottom: '1px solid lightgrey',
                        borderRadius: 1,
                        '&:hover': {
                            cursor: 'pointer',
                            backgroundColor: 'lightGrey.main',
                        },
                    }}
                    onClick={() => dispatch(push(`/survey/${params.id}/declaration/${params.idDeclaration}/point/${installation.id}`))}
                >
                    <Stack spacing={0.5} padding={1}>
                        <Grid container alignItems={'center'} spacing={1}>
                            <Typography sx={{ fontWeight: 'bold' }}>{installation.cityName || ''}</Typography>
                            {installation.stateCode === POINT_STATUS_DECLARATION.REMOVED && <Delete />}
                        </Grid>
                        <Typography>{installation.location || ''}</Typography>
                        <Grid container alignItems={'center'} >
                            {`${installation.code || ''} ${`- ${installation.parcel || ''} ${installation.section || ''}`}${installation.name ? ` - ${installation.name}` : ''}`}
                            {pointIsShared && (
                                <Tooltip title={tooltip}>
                                    <People sx={{ paddingLeft: 1, fontSize: 25 }} />
                                </Tooltip>
                            )}
                        </Grid>
                        <Typography>{`${i18n.requestedVolume} : ${formatMilliers(totalUsage) || 0} m3`}</Typography>
                    </Stack>
                </Grid>
            )
        }
        dispatch(ToastrAction.warning(i18n.errorWhenRetrievingSamplingPoints))
        return ''
    }

    const getFormattedPoint = (point) => {
        const installation = installationsWithGeo.find((i) => i.id === point.idInstallation)
        if (installation) {
            const city = citiesIndex[installation.townCode] || {}
            const link_pumps = declaration.link_exploitationMateriel.filter((m) => m.siteType === 7 && m.siteCode === installation.id)
            const link_counters = declaration.link_exploitationMateriel.filter((m) => m.siteType === 8 && link_pumps.find((lp) => lp.idVarious === m.siteCode))
            const matsName = [
                ...link_counters.map((lc) => lc.siteName),
                ...link_counters.map((lc) => (variousMateriels.find((m) => m.id === lc.idVarious) || {}).reference),
            ].reduce((acc, code) => `${acc ? `${acc}, ` : ''}${code || ''}`, '')
            let total = 0
            point.link_usages.forEach((u) => {
                total = total + (isNaN(parseInt(u.requestedYearVolume)) ? 0 : u.requestedYearVolume)
            })
            return {
                ...point,
                ...installation,
                stateCode: point.stateCode,
                link_usages: point.link_usages,
                cityName: city.name,
                matsName,
            }
        }
        dispatch(ToastrAction.warning(i18n.errorWhenRetrievingSamplingPoints))
        return undefined
    }

    const getHash = (point) => normalizeString(['townCode', 'location', 'name', 'cityName', 'parcel', 'section', 'code', 'matsName'].map(key => point?.[key]))

    const getFilteredData = (data) => {
        const includesValue = normalizeString(searchValue || '')
        return data.filter(i => getHash(i).includes(includesValue))
    }

    const getListPoints = (listPoints, singular, plurial, open) => {
        const formattedPoints = compact(getFilteredData(listPoints.map((p) => getFormattedPoint(p))))
        const orderedPoints = orderBy(compact(formattedPoints), ['townCode', 'cityName', 'parcel', 'section', 'code'])
        const orderedPointsFormatted = orderedPoints.map((point) => getPointCard(point))
        return (
            <Accordion defaultExpanded={open}>
                <AccordionSummary>
                    <Typography sx={{ fontSize: '1.5rem' }}>{listPoints.length} {`${getI18nTitleData(singular, plurial, listPoints)}`}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                    {!orderedPoints.length ?
                        <NoDataPanel />
                        :
                        orderedPointsFormatted
                    }
                </AccordionDetails>
            </Accordion>
        )
    }

    const { usedPoints, toDeletePoints, toAdjournPoints } = useMemo(() => ({
        usedPoints: selectedPoints.filter((p) => p.stateCode === POINT_STATUS_DECLARATION.USED),
        toDeletePoints: selectedPoints.filter((p) => p.stateCode === POINT_STATUS_DECLARATION.REMOVED),
        toAdjournPoints: selectedPoints.filter((p) => p.stateCode === POINT_STATUS_DECLARATION.ADJOURNED),
    }), [selectedPoints])

    return (
        <Stack spacing={1}>
            {pointsLoaded ? (
                <>
                    <Grid container justifyContent={'center'}>
                        <Grid size={4}>
                            <Input
                                title={ i18n.search }
                                value={ searchValue }
                                onChange={(v) => setSearchValue(v)}
                            />
                        </Grid>
                    </Grid>
                    {getListPoints(usedPoints, i18n.usedPoint, i18n.usedPoints, true)}
                    {getListPoints(toAdjournPoints, i18n.adjournPoint, i18n.adjournPoints)}
                    {getListPoints(toDeletePoints, i18n.deletePoint, i18n.deletePoints)}
                </>
            ) : (
                <ProgressCard indeterminate withMessage round />
            )}
        </Stack>
    )
}

export default DeclarationPointsStep
